import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Moment from "moment";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import { fetchSalesList } from "../../api/salesApi";
import { CustomersContext } from "../../context/customersContext";
import CustomerModal from "../modals/CustomerModal";
import { DataTable, PageHeader, Spinner, StatCard } from "../ui";

// ─── date range helpers ───────────────────────────────────────────────────────

const PRESETS = [
    { key: "this-month",   label: "This month" },
    { key: "last-month",   label: "Last month" },
    { key: "last-3",       label: "Last 3 months" },
    { key: "last-6",       label: "Last 6 months" },
    { key: "this-year",    label: "This year" },
    { key: "last-year",    label: "Last year" },
    { key: "all-time",     label: "All time" },
    { key: "custom",       label: "Custom" },
];

function resolveRange(preset) {
    const now = Moment();
    switch (preset) {
        case "this-month":
            return { start: now.clone().startOf("month"), end: now.clone().endOf("month") };
        case "last-month":
            return {
                start: now.clone().subtract(1, "month").startOf("month"),
                end:   now.clone().subtract(1, "month").endOf("month"),
            };
        case "last-3":
            return { start: now.clone().subtract(3, "months").startOf("month"), end: now.clone().endOf("day") };
        case "last-6":
            return { start: now.clone().subtract(6, "months").startOf("month"), end: now.clone().endOf("day") };
        case "this-year":
            return { start: now.clone().startOf("year"), end: now.clone().endOf("year") };
        case "last-year":
            return {
                start: now.clone().subtract(1, "year").startOf("year"),
                end:   now.clone().subtract(1, "year").endOf("year"),
            };
        case "all-time":
        default:
            return { start: null, end: null };
    }
}

// ─── aggregation helpers ──────────────────────────────────────────────────────

function sumPayments(sale) {
    return (sale.payments || []).reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0);
}

function sumCost(sale) {
    return (parseFloat(sale.cost) || 0) + (parseFloat(sale.labourCost) || 0);
}

function fmtCurrency(n) {
    return "$" + Math.round(n).toLocaleString();
}

function buildMonthlyBuckets(sales) {
    const map = {};
    sales.forEach((s) => {
        const key = Moment(s.orderDate).format("MMM YY");
        if (!map[key]) map[key] = { month: key, revenue: 0, profit: 0, payments: 0 };
        map[key].revenue  += parseFloat(s.salesPrice) || 0;
        map[key].profit   += (parseFloat(s.salesPrice) || 0) - sumCost(s);
        map[key].payments += sumPayments(s);
    });
    return Object.values(map).sort((a, b) =>
        Moment(a.month, "MMM YY").valueOf() - Moment(b.month, "MMM YY").valueOf()
    );
}

function buildByStatus(sales) {
    const map = {};
    sales.forEach((s) => {
        const st = s.workOrder?.status || "—";
        if (!map[st]) map[st] = { label: st, count: 0, revenue: 0, profit: 0, paid: 0 };
        map[st].count   += 1;
        map[st].revenue += parseFloat(s.salesPrice) || 0;
        map[st].profit  += (parseFloat(s.salesPrice) || 0) - sumCost(s);
        map[st].paid    += sumPayments(s);
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
}

function buildByProduct(sales) {
    const map = {};
    sales.forEach((s) => {
        const p = s.product || "—";
        if (!map[p]) map[p] = { label: p, count: 0, revenue: 0, profit: 0, paid: 0 };
        map[p].count   += 1;
        map[p].revenue += parseFloat(s.salesPrice) || 0;
        map[p].profit  += (parseFloat(s.salesPrice) || 0) - sumCost(s);
        map[p].paid    += sumPayments(s);
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
}

function buildByCustomer(sales, customers) {
    const map = {};
    sales.forEach((s) => {
        const id = s.customerId || "unknown";
        if (!map[id]) map[id] = { id, count: 0, revenue: 0, profit: 0, paid: 0 };
        map[id].count   += 1;
        map[id].revenue += parseFloat(s.salesPrice) || 0;
        map[id].profit  += (parseFloat(s.salesPrice) || 0) - sumCost(s);
        map[id].paid    += sumPayments(s);
    });
    return Object.values(map)
        .sort((a, b) => b.revenue - a.revenue)
        .map((row) => {
            const c = customers?.find((c) => c._id === row.id);
            const name = c
                ? (`${c.firstName || ""} ${c.lastName || ""}`.trim() || c.company || "—")
                : row.id === "unknown" ? "Unknown" : "—";
            return { ...row, label: name };
        });
}

// ─── sub-components ───────────────────────────────────────────────────────────


const SectionCard = ({ title, children }) => (
    <div className="rounded-xl border border-ink-100 bg-white p-4 sm:p-5">
        <h3 className="detail-section-title mb-4">{title}</h3>
        {children}
    </div>
);

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border border-ink-100 bg-white px-3 py-2 shadow-lg text-sm">
            <p className="mb-1 font-semibold text-ink-700">{label}</p>
            {payload.map((p) => (
                <p key={p.dataKey} style={{ color: p.color }}>
                    {p.name}: {fmtCurrency(p.value)}
                </p>
            ))}
        </div>
    );
};

// ─── breakdown table columns ──────────────────────────────────────────────────

const ShareCell = ({ row }) => (
    <div className="flex items-center justify-end gap-2">
        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-ink-100">
            <div
                className="h-full rounded-full bg-brand-500"
                style={{ width: `${(row.share || 0).toFixed(1)}%` }}
            />
        </div>
        <span className="w-10 text-right text-xs text-ink-400">{(row.share || 0).toFixed(1)}%</span>
    </div>
);

const makeColumns = (nameLabel) => [
    {
        id: "label",
        label: nameLabel,
        sortable: true,
        sortValue: (r) => r.label,
        cell: (r) => <span className="font-medium text-ink-800">{r.label}</span>,
    },
    {
        id: "count",
        label: "Orders",
        sortable: true,
        sortValue: (r) => r.count,
        align: "right",
        cell: (r) => <span className="tabular-nums text-ink-600">{r.count}</span>,
    },
    {
        id: "revenue",
        label: "Revenue",
        sortable: true,
        sortValue: (r) => r.revenue,
        align: "right",
        cell: (r) => <span className="tabular-nums font-semibold text-ink-900">{fmtCurrency(r.revenue)}</span>,
    },
    {
        id: "profit",
        label: "Profit",
        sortable: true,
        sortValue: (r) => r.profit,
        align: "right",
        cell: (r) => (
            <span className={`tabular-nums font-medium ${r.profit >= 0 ? "text-warning-600" : "text-danger-600"}`}>
                {fmtCurrency(r.profit)}
            </span>
        ),
    },
    {
        id: "paid",
        label: "Collected",
        sortable: true,
        sortValue: (r) => r.paid,
        align: "right",
        cell: (r) => <span className="tabular-nums text-success-600">{fmtCurrency(r.paid || 0)}</span>,
    },
    {
        id: "share",
        label: "Share",
        sortable: true,
        sortValue: (r) => r.share,
        align: "right",
        cell: (r) => <ShareCell row={r} />,
    },
];

// ─── main component ───────────────────────────────────────────────────────────

const SummaryReports = () => {
    const { customers, action } = useContext(CustomersContext);

    const [preset, setPreset]         = useState("this-year");
    const [customStart, setCustomStart] = useState("");
    const [customEnd,   setCustomEnd]   = useState("");
    const [sales,       setSales]       = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [error,       setError]       = useState(null);
    const [activeTab,        setActiveTab]        = useState("customer");
    const [customerOpen,     setCustomerOpen]     = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const handleCustomerClick = async (row) => {
        if (!row.id || row.id === "unknown") return;
        setCustomerOpen(true);
        const customer = await action.getCustomer(row.id);
        setSelectedCustomer(customer);
    };

    const handleCustomerClose = () => {
        setCustomerOpen(false);
        setSelectedCustomer(null);
    };

    // fetch all sales in parallel pages to avoid a single large request timing out
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        const PAGE_SIZE = 200;

        fetchSalesList({ page: 1, limit: PAGE_SIZE })
            .then(async (first) => {
                if (cancelled) return;
                const firstBatch = first?.salesList || [];
                const totalPages = first?.pagination?.totalPages || 1;

                if (totalPages <= 1) {
                    setSales(firstBatch);
                    return;
                }

                const remaining = Array.from({ length: totalPages - 1 }, (_, i) =>
                    fetchSalesList({ page: i + 2, limit: PAGE_SIZE }).then((d) => d?.salesList || [])
                );
                const rest = await Promise.all(remaining);
                if (!cancelled) setSales([...firstBatch, ...rest.flat()]);
            })
            .catch((err) => {
                if (!cancelled) setError(err.message || "Failed to load sales");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    // resolve date range
    const { start, end } = useMemo(() => {
        if (preset === "custom") {
            return {
                start: customStart ? Moment(customStart).startOf("day") : null,
                end:   customEnd   ? Moment(customEnd).endOf("day")     : null,
            };
        }
        return resolveRange(preset);
    }, [preset, customStart, customEnd]);

    // filter sales by date
    const filtered = useMemo(() => {
        return sales.filter((s) => {
            const d = Moment(s.orderDate);
            if (start && d.isBefore(start)) return false;
            if (end   && d.isAfter(end))    return false;
            return true;
        });
    }, [sales, start, end]);

    // aggregates
    const totalRevenue  = useMemo(() => filtered.reduce((a, s) => a + (parseFloat(s.salesPrice) || 0), 0), [filtered]);
    const totalCost     = useMemo(() => filtered.reduce((a, s) => a + sumCost(s), 0), [filtered]);
    const totalProfit   = totalRevenue - totalCost;
    const totalPaid     = useMemo(() => filtered.reduce((a, s) => a + sumPayments(s), 0), [filtered]);
    const totalBalance  = totalRevenue - totalPaid;
    const orderCount    = filtered.length;

    const monthly      = useMemo(() => buildMonthlyBuckets(filtered), [filtered]);
    const byStatus     = useMemo(() => buildByStatus(filtered),        [filtered]);
    const byProduct    = useMemo(() => buildByProduct(filtered),       [filtered]);
    const byCustomer   = useMemo(() => buildByCustomer(filtered, customers), [filtered, customers]);

    const rangeLabel = useMemo(() => {
        if (!start && !end) return "All time";
        if (start && end) return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
        if (start) return `From ${start.format("MMM D, YYYY")}`;
        return `Until ${end.format("MMM D, YYYY")}`;
    }, [start, end]);

    const withShare = useCallback(
        (rows) => rows.map((r) => ({ ...r, share: totalRevenue > 0 ? (r.revenue / totalRevenue) * 100 : 0 })),
        [totalRevenue],
    );

    const tabRows = useMemo(() => {
        const rows = activeTab === "customer" ? byCustomer
                   : activeTab === "product"  ? byProduct
                   : byStatus;
        return withShare(rows);
    }, [activeTab, byStatus, byProduct, byCustomer, withShare]);

    const tabColumns = useMemo(
        () =>
            activeTab === "customer" ? makeColumns("Customer")
          : activeTab === "product"  ? makeColumns("Product")
          : makeColumns("Status"),
        [activeTab],
    );

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-xl border border-danger-200 bg-danger-50 p-6 text-sm text-danger-700">
                {error}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader title="Reports" subtitle={rangeLabel} />

            {/* ── Date range ── */}
            <div className="rounded-xl border border-ink-100 bg-white p-3 sm:p-4">
                <div className="flex flex-wrap gap-2">
                    {PRESETS.map((p) => (
                        <button
                            key={p.key}
                            type="button"
                            onClick={() => setPreset(p.key)}
                            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                                preset === p.key
                                    ? "bg-brand-600 text-white"
                                    : "bg-ink-50 text-ink-600 hover:bg-ink-100"
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
                {preset === "custom" && (
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                            <label className="detail-field-label whitespace-nowrap">From</label>
                            <input
                                type="date"
                                value={customStart}
                                onChange={(e) => setCustomStart(e.target.value)}
                                className="rounded-lg border border-ink-200 px-3 py-1.5 text-sm text-ink-900"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="detail-field-label whitespace-nowrap">To</label>
                            <input
                                type="date"
                                value={customEnd}
                                onChange={(e) => setCustomEnd(e.target.value)}
                                className="rounded-lg border border-ink-200 px-3 py-1.5 text-sm text-ink-900"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* ── Stat cards ── */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard
                    icon={<i className="fas fa-dollar-sign" />}
                    label="Total Revenue"
                    value={fmtCurrency(totalRevenue)}
                    tone="info"
                    hint={`${orderCount} order${orderCount !== 1 ? "s" : ""}`}
                />
                <StatCard
                    icon={<i className="fas fa-chart-line" />}
                    label="Profit"
                    value={fmtCurrency(totalProfit)}
                    tone={totalProfit >= 0 ? "warning" : "danger"}
                    hint={totalRevenue > 0 ? `${((totalProfit / totalRevenue) * 100).toFixed(1)}% margin` : undefined}
                />
                <StatCard
                    icon={<i className="fas fa-check-circle" />}
                    label="Payments Received"
                    value={fmtCurrency(totalPaid)}
                    tone="success"
                />
                <StatCard
                    icon={<i className="fas fa-exclamation-circle" />}
                    label="Outstanding Balance"
                    value={fmtCurrency(totalBalance)}
                    tone={totalBalance > 0 ? "danger" : "success"}
                />
            </div>

            {/* ── Revenue chart ── */}
            <SectionCard title="Revenue over time">
                {monthly.length === 0 ? (
                    <p className="py-8 text-center text-sm text-ink-400">No data for this period.</p>
                ) : (
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={monthly} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                            <XAxis
                                dataKey="month"
                                tick={{ fontSize: 12, fill: "#64748b" }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                tick={{ fontSize: 12, fill: "#64748b" }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(v) => "$" + (v >= 1000 ? (v / 1000).toFixed(0) + "k" : v)}
                                width={48}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="revenue" name="Revenue" fill="#2c7aa0" radius={[4, 4, 0, 0]} maxBarSize={48} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </SectionCard>

            {/* ── Breakdown table ── */}
            <SectionCard title="Breakdown">
                {/* tab switcher */}
                <div className="mb-4 flex gap-1 rounded-lg bg-ink-50 p-1 w-fit">
                    {[
                        { key: "customer", label: "By Customer" },
                        { key: "product",  label: "By Product" },
                        { key: "status",   label: "By Status" },
                    ].map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setActiveTab(t.key)}
                            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                                activeTab === t.key
                                    ? "bg-brand-600 text-white shadow-sm"
                                    : "text-ink-500 hover:text-ink-700"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <DataTable
                    rows={tabRows}
                    columns={tabColumns}
                    rowKey="label"
                    defaultSort={{ id: "revenue", direction: "desc" }}
                    emptyMessage="No data for this period."
                    onRowClick={activeTab === "customer" ? handleCustomerClick : undefined}
                />
            </SectionCard>

            <CustomerModal
                open={customerOpen}
                selectedCustomer={selectedCustomer}
                handleClose={handleCustomerClose}
                handleClickOpen={() => setCustomerOpen(true)}
            />
        </div>
    );
};

export default SummaryReports;
